/**
 * Changing your character, mid-game. SPEC.md §46, §8.6, §6.3.
 *
 * Everybody's, not the host's. An avatar is how a table of two Mikes tells which
 * seat is whose (§34.3), so it is the one piece of identity a player has to be
 * able to fix themselves — and picking one badly at the join form should not
 * cost anybody the night. Every other per-player setting still lives on the join
 * form; this is the exception because the join form is exactly where the mistake
 * gets made. §5.1, §49
 *
 * **One character per player, and this is the second place that rule is kept.**
 * The server is the one that enforces it — `assertAvatarFree` runs inside the
 * update's own transaction and refuses a face somebody active already has,
 * exempting the wearer so re-saving is not a clash with oneself. What happens
 * here is presentation: the taken faces are greyed and dropped from arrow-key
 * walking, so a player is not offered a choice that would come back as an error.
 * The check is not repeated in this file, because a rule implemented twice is a
 * rule that will drift. §46, §60
 *
 * **Freeing and blocking need no code at all**, and that is the point of reading
 * the roster rather than keeping a list. `takenKeys` is derived from whatever
 * every *other* active player is wearing right now, so the moment somebody
 * swaps, the face they left goes selectable and the one they took goes grey — on
 * every device, off the same sequenced `player:updated` broadcast that moved
 * them. A removed player gives theirs back for the same reason: they stop being
 * an active player. §46, §31, §15.2
 */

import { useEffect, useState } from 'react';
import { Avatar } from '@/components/ui/Avatar';
import { AvatarPicker } from '@/features/session/AvatarPicker';
import { Sheet } from '@/components/ui/Sheet';
import { useSession } from '@/hooks/useSession';
import { useMe, usePlayersBySeat } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';

export function ChangeAvatarSheet() {
  const open = useUiStore((state) => state.avatarPickerOpen);
  const close = useUiStore((state) => state.closeAvatarPicker);
  const me = useMe();
  const players = usePlayersBySeat();
  const { updateProfile, interactive } = useSession();

  const [choice, setChoice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /**
   * How many picks this sheet has taken, and the preview is remounted on it —
   * the join form's `AvatarPreview` nonce, which explains why. Counted rather
   * than keyed on the avatar because re-picking the character already shown is
   * still a tap, and a tap that produces nothing reads as one that was missed.
   * §6.3, §46
   */
  const [picks, setPicks] = useState(0);

  /**
   * Forgotten on the way *out*, not seeded on the way in. §46
   *
   * Seeding it when the sheet opens looks equivalent and is not: the effect runs
   * after the first render, so the picker mounts holding the choice abandoned
   * last time — and `AvatarPicker` scrolls its grid to the selected tile on
   * mount only, so the player opens on somebody else's character and the grid is
   * already scrolled to it before the correction lands. Clearing on close means
   * `choice` is null by the time it opens, and the first frame is already right.
   */
  useEffect(() => {
    if (!open) {
      setChoice(null);
      setPicks(0);
    }
  }, [open]);

  if (!me) return null;

  const selected = choice ?? me.avatarKey;
  // Everyone else at the table. Their own face is not "taken" — see the note
  // above about the wearer's exemption. §46
  const takenKeys = players
    .filter((player) => player.id !== me.id)
    .map((player) => player.avatarKey);

  /**
   * Compared raw, not through `avatarFor`. §46
   *
   * `avatarFor` collapses every unresolvable key to the fallback, so a player
   * left on a key that no longer exists — which is what renumbering the set does
   * to a live table — would read as "already wearing" the fallback character and
   * the one tap that could fix them would be treated as a no-op.
   */
  const unchanged = selected === me.avatarKey;

  const save = async (): Promise<void> => {
    if (unchanged) {
      close();
      return;
    }
    setSaving(true);
    const ok = await updateProfile({ avatarKey: selected });
    setSaving(false);
    if (ok) {
      close();
      return;
    }
    /**
     * Refused — somebody took this face between the render and the tap, and the
     * toast already says so in the server's own copy. §35
     *
     * The choice is dropped rather than kept, and that matters: `AvatarPicker`
     * exempts the *selected* key from the taken list (so re-saving your own face
     * is not a clash with yourself), so leaving the rejected key selected would
     * render the one tile the server has just refused as checked and available
     * — an invitation to tap it again. Falling back to what they are actually
     * wearing puts it back among the greyed ones. §46
     *
     * Counted as a pick, so the preview animates back rather than silently
     * becoming a different character at the moment the player has just been
     * told no. §6.3, §35
     */
    setChoice(null);
    setPicks((count) => count + 1);
  };

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Change your character"
      description="Nobody else can be wearing it. Greyed-out characters are taken."
      /**
       * The character being chosen, standing on the sheet. §46, §6.3
       *
       * A tile in a grid of 112 is about eleven millimetres of a character
       * that was drawn to be looked at, and the room to look at one properly was
       * already on screen — the dimmed band above the sheet, which until now was
       * only the thing you tapped to get out. So the pick is shown at the size
       * the join form shows it at, on the surface the player is already looking
       * at, without the sheet giving up a single row of its grid. §19
       *
       * Four fifths of the band with the last fifth left empty above it, which
       * is the join form's proportion and for its reason: the stretch overshoots
       * upward out of the character's own feet, and a preview that filled its box
       * would send the tall frame of it off the top of the screen — losing the
       * head at the exact moment the character is being shown off. §6.3, §18.2
       *
       * Bounded at both ends, because the band is leftover space and leftover
       * space is not a size.
       *
       * The **224 px ceiling is the artwork's own resolution** — the seat file is
       * `AVATAR_IMAGE_PIXELS` square, so past this it is being invented rather
       * than shown, and on a 430 px phone four fifths of the band would ask for
       * 301. It is the seat file rather than the 768 px celebration variant for
       * the join form's reason: the tile they just tapped has this one decoded
       * already, so the preview draws in the same frame as the tap instead of
       * fetching 60 KB per character on a screen made for flicking through 112.
       * The ceiling is what keeps that trade honest. §46
       *
       * The **floor is in the stylesheet**, because it is a question about the
       * viewport rather than about this component: the band shrinks with the
       * screen while the tile it previews does not, so past a point the preview
       * stops being worth the space. `.avatar-sheet-preview` turns it off there
       * and carries the measurements. Measured across §18.1 it draws between 2.2
       * and 4.6 times a tile, and 224 px at the top three widths. §18.2
       *
       * The drop-shadow is the one thing here the join form does not have, and
       * the backdrop is why: that preview sits on an opaque page, this one on a
       * dimmed but busy score screen — a roster of other characters and a keypad.
       * It is a `drop-shadow` filter rather than anything drawn behind the
       * artwork, so it follows the alpha and traces the silhouette; a shape the
       * character does not have is exactly what §46 keeps off a cut-out sticker.
       */
      aside={
        <Avatar
          key={picks}
          avatarKey={selected}
          className="avatar-sheet-preview avatar-preview-stretch mx-auto h-4/5 max-h-[224px] w-auto max-w-[70%] object-bottom drop-shadow-[0_10px_18px_rgba(0,0,0,0.45)]"
        />
      }
      footer={
        <div className="grid grid-cols-[1fr_1.2fr] gap-2">
          <button
            type="button"
            onClick={close}
            className="min-h-tap rounded-control border border-line bg-surface font-semibold text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            data-autofocus
            disabled={!interactive || saving}
            onClick={() => {
              void save();
            }}
            className="min-h-tap rounded-control bg-accent font-semibold text-accent-ink disabled:opacity-45"
          >
            {saving ? 'Saving…' : unchanged ? 'Done' : 'Use this one'}
          </button>
        </div>
      }
    >
      <AvatarPicker
        value={selected}
        // Every tap counts, including a tap on the character already shown —
        // see `picks`. §6.3
        onChange={(avatarKey) => {
          setChoice(avatarKey);
          setPicks((count) => count + 1);
        }}
        disabled={!interactive || saving}
        takenKeys={takenKeys}
      />
    </Sheet>
  );
}
